/**
 * Bedrijf zonder account aan een account koppelen (Joris 28 sep). Legt de koppeling vast in
 * Airtable (Accounts → Bedrijven, alleen toevoegen); daarna tellen omzet, offertes en lopend
 * werk van het bedrijf bij dat account. Vangnet: undo-toast die precies deze koppeling weer
 * weghaalt (budgetApi.ontkoppel met record-id + wijziging-id; de engine weigert als de koppeling
 * intussen veranderde). Budget dat al op de losse regel stond verhuist niet vanzelf:
 * dat komt onder "Budget overzetten" te staan (wees), en dat zeggen we vooraf.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';
import { euro, koppelbareAccounts, koppelSuggestie } from './budgetLogica';

export const companyIdVan = (sleutel) => {
  const m = /^bedrijf:(\d{1,12})$/.exec(sleutel || '');
  return m ? Number(m[1]) : null;
};

export default function KoppelDialoog({ regel, regels, jaar, onClose, onGekoppeld }) {
  const accounts = useMemo(() => koppelbareAccounts(regels), [regels]);
  const suggestie = useMemo(() => (regel ? koppelSuggestie(regel.naam, accounts) : null), [regel, accounts]);
  const [keuze, setKeuze] = useState(null);
  const [bezig, setBezig] = useState(false);
  useEffect(() => { setKeuze(suggestie); }, [suggestie]);

  if (!regel) return null;
  const companyId = companyIdVan(regel.sleutel);
  // Elke opgeslagen budgetrij (versie > 0) wordt na het koppelen een wees, ook zonder bedrag.
  const ingevuld = [
    regel.target != null && `een target (${euro(regel.target)})`,
    regel.target_vorig != null && `een target vorig jaar (${euro(regel.target_vorig)})`,
    regel.rf02 != null && `RF02 (${euro(regel.rf02)})`,
    regel.getekend != null && `getekend (${euro(regel.getekend)})`,
    regel.notitie && 'een opmerking',
  ].filter(Boolean).join(', ') || (regel.versie > 0 ? 'ingevuld budget' : '');
  const heeftBudget = !!ingevuld;

  const koppel = async () => {
    if (!keuze || !companyId) return;
    setBezig(true);
    try {
      const uit = await budgetApi.koppel(companyId, keuze.sleutel, jaar);
      await onGekoppeld();
      onClose();
      if (uit?.gewijzigd === false) {
        toast.success(`${regel.naam} hing al aan ${keuze.naam}`);
        return;
      }
      const herstel = {
        companyId, accountSleutel: keuze.sleutel, bedrijfRecordId: uit.bedrijf_record_id, wijzigingId: uit.wijziging_id,
      };
      biedOngedaanAan(`${regel.naam} gekoppeld aan ${keuze.naam}`, async () => {
        await budgetApi.ontkoppel(herstel, jaar);
        await onGekoppeld();
      }, { eigenKnop: true });
    } catch (e) {
      toast.error(`Koppelen mislukt: ${e.message}`);
    } finally { setBezig(false); }
  };

  return (
    <Dialog open onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Koppel aan account</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          <b>{regel.naam}</b> heeft nog geen account. Kies het account waar dit bedrijf bij hoort.
        </Typography>
        <Autocomplete
          options={accounts}
          value={keuze}
          onChange={(_, v) => setKeuze(v)}
          getOptionLabel={(a) => a.naam || ''}
          isOptionEqualToValue={(a, b) => a.sleutel === b.sleutel}
          filterOptions={(opties, { inputValue }) => {
            const z = inputValue.trim().toLowerCase();
            return !z ? opties : opties.filter((a) => [a.naam, ...(a.bedrijfsnamen || [])]
              .some((n) => (n || '').toLowerCase().includes(z)));
          }}
          renderOption={(props, a) => (
            <li {...props} key={a.sleutel}>
              <Box>
                <Typography sx={{ fontSize: 14 }}>{a.naam}</Typography>
                {a.bedrijfsnamen?.length > 0 && (
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{a.bedrijfsnamen.join(', ')}</Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(p) => <TextField {...p} label="Account" size="small" autoFocus
            helperText={suggestie && keuze?.sleutel === suggestie.sleutel ? 'Voorstel op basis van de naam' : ' '} />}
        />
        {keuze && (
          <Typography sx={{ fontSize: 13.5, color: '#475569', mt: 1 }}>
            De omzet, offertes en het lopende werk van {regel.naam} tellen daarna bij {keuze.naam}.
            De koppeling wordt vastgelegd in Airtable (Accounts → Bedrijven).
          </Typography>
        )}
        {heeftBudget && (
          <Alert severity="info" sx={{ mt: 2, fontSize: 13.5 }}>
            Op deze regel staat al {ingevuld}. Dat verhuist niet vanzelf: na het koppelen blijft het staan onder
            {' '}"Budget overzetten (telt niet mee)". Zet het bedrag zelf bij {keuze ? keuze.naam : 'het account'} en maak de oude regel leeg.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={koppel} disabled={!keuze || !companyId || bezig}
          sx={{ textTransform: 'none', fontWeight: 700 }}>{bezig ? 'Bezig…' : 'Koppel'}</Button>
      </DialogActions>
    </Dialog>
  );
}
